import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PromoService from '../../services/promoService';
import { useAuth } from '../../hooks/useAuth';
import { validateImageUrl, getInitials } from '../../utils/imageUtils';

const PromotionalWebsite = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [settings, setSettings] = useState(null);
  const [statistics, setStatistics] = useState({ songCount: 0, musicalCount: 0, talentCount: 0 });
  const [featuredTalent, setFeaturedTalent] = useState([]);
  const [talentRoles, setTalentRoles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [isUploadingSlideshow, setIsUploadingSlideshow] = useState(false);
  const [currentTalentIndex, setCurrentTalentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [validatedTalentImages, setValidatedTalentImages] = useState(new Map());
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    loadPromotionalData();
  }, []);

  // Slideshow auto-play effect
  useEffect(() => {
    if (slideshowImages?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slideshowImages?.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [slideshowImages]);

  // Validate talent images after they're loaded
  useEffect(() => {
    if (featuredTalent && featuredTalent?.length > 0) {
      validateTalentImages();
    }
  }, [featuredTalent]);

  /**
   * Validates all talent headshot images
   */
  const validateTalentImages = async () => {
    const imageValidations = new Map();
    
    for (const talent of featuredTalent) {
      if (talent?.headshot_url) {
        const isValid = await validateImageUrl(talent?.headshot_url);
        imageValidations?.set(talent?.id, isValid ? talent?.headshot_url : null);
      } else {
        imageValidations?.set(talent?.id, null);
      }
    }
    
    setValidatedTalentImages(imageValidations);
  };

  /**
   * Gets the appropriate image URL for a talent with fallback handling
   */
  const getTalentImageUrl = (talent) => {
    const validatedUrl = validatedTalentImages?.get(talent?.id);
    
    // If validation hasn't completed yet, show the original URL
    if (!validatedTalentImages?.has(talent?.id)) {
      return talent?.headshot_url || null;
    }
    
    // Return validated URL (null if invalid)
    return validatedUrl;
  };

  /**
   * Renders talent image with proper fallback
   */
  const renderTalentImage = (talent) => {
    const imageUrl = getTalentImageUrl(talent);
    const talentName = talent?.display_name || `${talent?.first_name} ${talent?.last_name}`;
    const initials = getInitials(talent?.first_name, talent?.last_name);
    
    if (imageUrl) {
      return (
        <img 
          src={imageUrl} 
          alt={`${talentName} - Professional musical theatre performer and instructor`}
          className="w-full h-full object-cover rounded-xl"
          onError={(e) => {
            // Fallback if image fails to load even after validation
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    // Show initials placeholder if no valid image
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-6xl font-heading font-bold text-white opacity-90">
          {initials || '?'}
        </div>
      </div>
    );
  };

  /**
   * Handles next talent card navigation
   */
  const handleNextTalent = () => {
    if (featuredTalent?.length > 0) {
      setCurrentTalentIndex((prev) => {
        // Calculate how many cards fit in the viewport
        const cardWidth = window.innerWidth < 768 ? 240 : 280;
        const visibleCards = Math.floor(window.innerWidth / cardWidth);
        // Calculate maximum index to keep carousel full
        const maxIndex = Math.max(0, featuredTalent?.length - visibleCards);
        
        const nextIndex = prev + 1;
        // Stop at the last position that keeps carousel full
        return Math.min(nextIndex, maxIndex);
      });
    }
  };

  /**
   * Handles previous talent card navigation
   */
  const handlePrevTalent = () => {
    if (featuredTalent?.length > 0) {
      setCurrentTalentIndex((prev) => {
        const prevIndex = prev - 1;
        // Stop at the start (don't loop)
        return Math.max(prevIndex, 0);
      });
    }
  };

  /**
   * Checks if carousel is at the end position
   */
  const isAtEnd = () => {
    if (featuredTalent?.length === 0) return false;
    const cardWidth = window.innerWidth < 768 ? 240 : 280;
    const visibleCards = Math.floor(window.innerWidth / cardWidth);
    const maxIndex = Math.max(0, featuredTalent?.length - visibleCards);
    return currentTalentIndex >= maxIndex;
  };

  /**
   * Checks if carousel is at the start position
   */
  const isAtStart = () => {
    return currentTalentIndex === 0;
  };

  /**
   * Handles touch start for mobile swipe
   */
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e?.targetTouches?.[0]?.clientX);
  };

  /**
   * Handles touch move for mobile swipe
   */
  const handleTouchMove = (e) => {
    setTouchEnd(e?.targetTouches?.[0]?.clientX);
  };

  /**
   * Handles touch end for mobile swipe
   */
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleNextTalent();
    }
    if (isRightSwipe) {
      handlePrevTalent();
    }
  };

  const loadPromotionalData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [settingsResult, statsResult, talentResult] = await Promise.all([
        PromoService?.getPromoSettings(),
        PromoService?.getPromoStatistics(),
        PromoService?.getFeaturedTalent(8)
      ]);

      if (settingsResult?.success) {
        setSettings(settingsResult?.data);
        
        // Load slideshow images - prioritize storage bucket images for guaranteed loading
        const slideshow_images = settingsResult?.data?.slideshow_images;
        if (slideshow_images && Array.isArray(slideshow_images) && slideshow_images?.length > 0) {
          // Filter out invalid images (empty URLs)
          const validImages = slideshow_images?.filter(img => img?.url && img?.url?.trim() !== '');
          
          if (validImages?.length > 0) {
            setSlideshowImages(validImages);
          } else {
            // If all filtered out, ensure empty array
            setSlideshowImages([]);
          }
        } else {
          // If no images in settings, ensure empty array (will show placeholder)
          setSlideshowImages([]);
        }
      } else {
        throw new Error(settingsResult?.error || 'Failed to load settings');
      }

      if (statsResult?.success) {
        setStatistics(statsResult?.data);
      }

      if (talentResult?.success) {
        setFeaturedTalent(talentResult?.data || []);
        
        // Fetch roles for each talent
        const rolesMap = {};
        for (const talent of talentResult?.data || []) {
          const rolesResult = await PromoService?.getTalentRoles(talent?.id);
          if (rolesResult?.success) {
            rolesMap[talent?.id] = rolesResult?.data || [];
          }
        }
        setTalentRoles(rolesMap);
      }

    } catch (err) {
      console.error('Error loading promotional data:', err);
      setError(err?.message || 'Failed to load promotional content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = () => {
    navigate('/auth/register');
  };

  const handleDownloadApp = () => {
    const downloadSection = document.getElementById('download');
    downloadSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTalentClick = (talentId) => {
    navigate(`/talent-detail/${talentId}`);
  };

  const handleSignOut = async () => {
    try {
      setShowUserMenu(false);
      
      // Sign out from Supabase - error handling is done in AuthContext
      await signOut();
      
      // Navigate to login page regardless of sign out result
      navigate('/login-page', { replace: true });
    } catch (error) {
      // Even if there's an error, navigate to login since local state is cleared
      console.error('Sign out navigation error:', error);
      navigate('/login-page', { replace: true });
    }
  };

  const handleMyAccount = () => {
    setShowUserMenu(false);
    navigate('/my-account');
  };

  const handleMyStage = () => {
    setShowUserMenu(false);
    navigate('/the-stage-student-dashboard');
  };

  const handleAdminDashboard = () => {
    setShowUserMenu(false);
    navigate('/dashboard');
  };

  const handleAppDownloadClick = (e) => {
    e?.preventDefault();
    
    // Check if device is mobile
    const isMobile = /iPhone|iPad|iPod|Android/i?.test(navigator?.userAgent);
    
    if (isMobile) {
      setShowMobilePopup(true);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center p-6">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 max-w-md">
          <Icon name="AlertTriangle" size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white text-center mb-2">
            Unable to Load Content
          </h2>
          <p className="text-blue-100 text-center mb-6">{error || 'An error occurred'}</p>
          <Button onClick={loadPromotionalData} className="w-full" iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const heroSection = settings?.hero_section || {};
  const features = settings?.features || [];
  const pricing = settings?.pricing || {};
  const schoolPricing = settings?.school_pricing || null;
  const testimonials = settings?.testimonials || [];
  const faqs = settings?.faqs || [];
  const contact = settings?.contact || {};
  const statsDisplay = settings?.stats_display || {};
  const heroVideoUrl = settings?.hero_video_url || '';

  return (
    <>
      <Helmet>
        <title>RoleCall - Master Musical Theatre with Interactive Lessons</title>
        <meta name="description" content="RoleCall - Gamified musical theatre learning with professional coaching from Broadway performers. $9.99/month with unlimited access." />
        <meta property="og:title" content="RoleCall - Master Musical Theatre" />
        <meta property="og:description" content="Interactive lessons, professional video content, and personalized learning paths for musical theatre enthusiasts" />
      </Helmet>
      <div className={`min-h-screen bg-gradient-to-br ${heroSection?.background_gradient || 'from-indigo-900 via-blue-900 to-purple-900'}`}>
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background Slideshow */}
          <div className="absolute inset-0 z-0">
            {slideshowImages && slideshowImages?.length > 0 ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.6 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1 }}
                  className="absolute inset-0"
                >
                  <img
                    src={slideshowImages?.[currentSlide]?.url}
                    alt={slideshowImages?.[currentSlide]?.alt || `Musical theatre slideshow image ${currentSlide + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-blue-900/40 to-purple-900/40" />
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900" />
            )}
          </div>

          {/* Navigation Bar - FIXED Z-INDEX AND DROPDOWN */}
          <nav className="relative z-50 container mx-auto px-6 py-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AppImage 
                src="/assets/images/rolecall_logo_wide-1756786874531.png" 
                alt="RoleCall Logo - Musical Theatre Learning Platform"
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <div className="flex items-center space-x-4 relative z-50">
              <Button
                variant="outline"
                onClick={() => navigate('/hiring-page')}
                className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
                iconName="Briefcase"
                iconPosition="left"
              >
                We're Hiring
              </Button>
              
              {!user ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/login-page')}
                    className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={handleSignup}
                    className="bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full px-6 py-2"
                  >
                    Sign Up
                  </Button>
                </>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-full px-4 py-2 hover:bg-opacity-20 transition-all relative z-50"
                  >
                    {userProfile?.profile_photo_url ? (
                      <img
                        src={userProfile?.profile_photo_url}
                        alt={userProfile?.full_name || 'User profile'}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Icon name="User" size={18} className="text-white" />
                      </div>
                    )}
                    <span className="text-white font-medium hidden md:block">
                      {userProfile?.full_name || userProfile?.username || user?.email}
                    </span>
                    <Icon 
                      name="ChevronDown" 
                      size={18} 
                      className={`text-white transition-transform ${showUserMenu ? 'rotate-180' : ''}`} 
                    />
                  </button>

                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-[60]">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">
                          {userProfile?.full_name || userProfile?.username}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </p>
                        {userProfile?.role && userProfile?.role !== 'user' && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
                            {userProfile?.role}
                          </span>
                        )}
                      </div>
                      
                      <div className="py-2">
                        <button
                          onClick={handleMyAccount}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center space-x-3"
                        >
                          <Icon name="User" size={16} className="text-gray-400" />
                          <span>My Account</span>
                        </button>
                        
                        <button
                          onClick={handleMyStage}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center space-x-3"
                        >
                          <Icon name="Home" size={16} className="text-gray-400" />
                          <span>My Stage</span>
                        </button>
                        
                        {userProfile?.role === 'superuser' && (
                          <button
                            onClick={handleAdminDashboard}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center space-x-3"
                          >
                            <Icon name="Settings" size={16} className="text-gray-400" />
                            <span>Admin Dashboard</span>
                          </button>
                        )}
                        
                        <button
                          onClick={handleSignOut}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-3"
                        >
                          <Icon name="LogOut" size={16} className="text-red-600" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>

          {/* Hero Content - KEEP LOWER Z-INDEX */}
          <div className="relative z-10 container mx-auto px-6 py-20 lg:py-32">
            <div className="grid grid-cols-1 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center px-8 md:px-16 lg:px-24"
              >
                <h1 className="text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight px-12 sm:px-24 md:px-32 lg:px-40">
                  {heroSection?.headline || 'Master Musical Theatre with RoleCall'}
                </h1>
                <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                  {heroSection?.subtitle || 'Interactive lessons, gamified learning, and professional coaching from Broadway performers. Start your musical theatre journey today!'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="large"
                    onClick={handleSignup}
                    className="bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full px-8 py-4"
                    iconName="Sparkles"
                    iconPosition="left"
                  >
                    {heroSection?.cta_primary_text || 'Sign Up'}
                  </Button>
                  <Button
                    size="large"
                    variant="outline"
                    onClick={handleDownloadApp}
                    className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-8 py-4"
                    iconName="Download"
                    iconPosition="left"
                  >
                    {heroSection?.cta_secondary_text || 'Download App'}
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Statistics */}
            {statsDisplay?.show_song_count && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-8"
              >
                {statsDisplay?.show_song_count && (
                  <div className="text-center">
                    <div className="text-5xl font-heading font-bold text-white mb-2">
                      {statistics?.songCount?.toLocaleString()}+
                    </div>
                    <div className="text-blue-100">{statsDisplay?.song_count_label || 'Songs'}</div>
                  </div>
                )}
                {statsDisplay?.show_musical_count && (
                  <div className="text-center">
                    <div className="text-5xl font-heading font-bold text-white mb-2">
                      {statistics?.musicalCount?.toLocaleString()}+
                    </div>
                    <div className="text-blue-100">{statsDisplay?.musical_count_label || 'Musicals'}</div>
                  </div>
                )}
                {statsDisplay?.show_talent_count && (
                  <div className="text-center">
                    <div className="text-5xl font-heading font-bold text-white mb-2">
                      {statistics?.talentCount?.toLocaleString()}+
                    </div>
                    <div className="text-blue-100">{statsDisplay?.talent_count_label || 'Professional Instructors'}</div>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white bg-opacity-5">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-white mb-4">
                Everything You Need to Excel
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Professional tools and resources designed for musical theatre enthusiasts at every level
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features && features?.length > 0 ? (
                features?.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6 hover:bg-opacity-15 transition-all"
                  >
                    <div className={`bg-gradient-to-r ${feature?.color || 'from-purple-500 to-pink-500'} w-16 h-16 rounded-xl flex items-center justify-center mb-4`}>
                      <Icon name={feature?.icon || 'Star'} size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-white mb-3">
                      {feature?.title}
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      {feature?.description}
                    </p>
                  </motion.div>
                ))
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6 hover:bg-opacity-15 transition-all"
                  >
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                      <Icon name="Gamepad2" size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-white mb-3">
                      Gamified Learning
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      Interactive lessons with progress tracking, badges, and level progression for musical theatre skills
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6 hover:bg-opacity-15 transition-all"
                  >
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                      <Icon name="Video" size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-white mb-3">
                      Professional Video Content
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      High-quality instructional videos featuring Broadway and West End performers teaching technique and character development
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6 hover:bg-opacity-15 transition-all"
                  >
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                      <Icon name="Music" size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-white mb-3">
                      Comprehensive Song Library
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      Detailed analysis of musical theatre repertoire with character breakdowns, vocal coaching, and performance notes
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6 hover:bg-opacity-15 transition-all"
                  >
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                      <Icon name="Target" size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-white mb-3">
                      Personalized Learning Paths
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      Tailored curriculum based on vocal range, experience level, and musical preferences
                    </p>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Talent Showcase Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-white mb-4">
                Learn from the Best
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Professional musical theatre actors and coaches from Los Angeles, New York and London.
              </p>
            </div>

            {featuredTalent && featuredTalent?.length > 0 ? (
              <div className="relative">
                {/* Left Navigation Arrow - Web Only - Conditionally shown */}
                {!isAtStart() && (
                  <button
                    onClick={handlePrevTalent}
                    className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-full p-3 hover:bg-opacity-20 transition-all"
                    aria-label="Previous talent"
                  >
                    <Icon name="ChevronLeft" size={24} className="text-white" />
                  </button>
                )}

                {/* Right Navigation Arrow - Web Only - Conditionally shown */}
                {!isAtEnd() && (
                  <button
                    onClick={handleNextTalent}
                    className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-full p-3 hover:bg-opacity-20 transition-all"
                    aria-label="Next talent"
                  >
                    <Icon name="ChevronRight" size={24} className="text-white" />
                  </button>
                )}

                {/* Carousel Container */}
                <div className="relative overflow-hidden">
                  {/* Left gradient fade */}
                  <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-[#6e32b9] to-transparent z-10 pointer-events-none" />
                  
                  {/* Talent Cards Container with Touch Support */}
                  <div 
                    className="overflow-hidden"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div
                      className="flex gap-4 md:gap-6 transition-transform duration-300 ease-out"
                      style={{
                        transform: `translateX(-${currentTalentIndex * (window.innerWidth < 768 ? 240 : 280)}px)`
                      }}
                    >
                      {/* Render unique talent cards only */}
                      {featuredTalent?.map((talent, index) => {
                        const roles = talentRoles?.[talent?.id] || [];
                        const talentName = talent?.display_name || `${talent?.first_name} ${talent?.last_name}`;
                        const initials = getInitials(talent?.first_name, talent?.last_name);
                        
                        return (
                          <div
                            key={talent?.id}
                            onClick={() => handleTalentClick(talent?.id)}
                            className="flex-shrink-0 w-56 md:w-64 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-4 hover:bg-opacity-15 transition-all cursor-pointer"
                          >
                            <div className="bg-gradient-to-br from-purple-500 to-pink-500 w-full aspect-square rounded-xl mb-3 flex items-center justify-center relative overflow-hidden">
                              {renderTalentImage(talent)}
                              {/* Hidden fallback for onError handler */}
                              <div 
                                className="absolute inset-0 hidden items-center justify-center"
                                style={{ display: 'none' }}
                              >
                                <div className="text-6xl font-heading font-bold text-white opacity-90">
                                  {initials || '?'}
                                </div>
                              </div>
                            </div>
                            <h4 className="text-lg font-heading font-semibold text-white mb-1 text-center">
                              {talentName}
                            </h4>
                            {roles?.length > 0 && (
                              <div className="text-sm text-blue-200 text-center mb-2">
                                Teaching: {roles?.slice(0, 2)?.map(r => r?.character_name)?.join(', ')}
                                {roles?.length > 2 && ` +${roles?.length - 2} more`}
                              </div>
                            )}
                            <div className="flex items-center justify-center space-x-2 mt-2">
                              {talent?.instagram && (
                                <a 
                                  href={talent?.instagram} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-200 hover:text-white transition-colors"
                                  aria-label={`Visit ${talentName}'s Instagram`}
                                  onClick={(e) => e?.stopPropagation()}
                                >
                                  <Icon name="Instagram" size={16} />
                                </a>
                              )}
                              {talent?.website && (
                                <a 
                                  href={talent?.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-200 hover:text-white transition-colors"
                                  aria-label={`Visit ${talentName}'s website`}
                                  onClick={(e) => e?.stopPropagation()}
                                >
                                  <Icon name="Globe" size={16} />
                                </a>
                              )}
                              {talent?.youtube && (
                                <a 
                                  href={talent?.youtube} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-200 hover:text-white transition-colors"
                                  aria-label={`Visit ${talentName}'s YouTube channel`}
                                  onClick={(e) => e?.stopPropagation()}
                                >
                                  <Icon name="Youtube" size={16} />
                                </a>
                              )}
                              {talent?.tiktok && (
                                <a 
                                  href={talent?.tiktok} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-200 hover:text-white transition-colors"
                                  aria-label={`Visit ${talentName}'s TikTok`}
                                  onClick={(e) => e?.stopPropagation()}
                                >
                                  <Icon name="Music" size={16} />
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Right gradient fade */}
                  <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-[#32215d] to-transparent z-10 pointer-events-none" />
                </div>

                {/* Instructional Text */}
                <div className="text-center mt-6">
                  <p className="text-blue-200 text-sm">
                    Click on a Coach to learn more about them.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="Users" size={64} className="text-white opacity-30 mx-auto mb-4" />
                <p className="text-xl text-blue-100">
                  Featured talent profiles coming soon
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-white bg-opacity-5" id="pricing">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-white mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-blue-100 mb-4">
                Everything you need to master musical theatre
              </p>
              <p className="text-lg text-blue-200 max-w-2xl mx-auto">
                Get RoleCall for less than the price of one hour of vocal coaching - unlimited access to hundreds of professional lessons and songs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Individual Pricing - Reduced size */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-purple-600 to-pink-500 rounded-2xl p-6 shadow-2xl"
              >
                <div className="text-center mb-4">
                  <div className="text-4xl font-heading font-bold text-white mb-2">
                    {pricing?.currency_symbol || '$'}{pricing?.monthly_price || '9.99'}
                    <span className="text-lg font-normal">/month</span>
                  </div>
                  <p className="text-white text-opacity-90">Individual Access</p>
                </div>

                <ul className="space-y-2 mb-6">
                  {pricing?.features?.map((feature, index) => (
                    <li key={index} className="flex items-center text-white text-sm">
                      <Icon name="Check" size={18} className="mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="large"
                  onClick={handleSignup}
                  className="w-full bg-white text-purple-600 hover:bg-opacity-90 rounded-xl"
                  iconName="Sparkles"
                  iconPosition="left"
                >
                  {pricing?.cta_text || 'Start Your Journey'}
                </Button>
              </motion.div>

              {/* Special School Pricing - New gradient box */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-500 rounded-2xl p-6 shadow-2xl"
              >
                <div className="text-center mb-4">
                  <div className="text-4xl font-heading font-bold text-white mb-2">
                    Special Pricing
                  </div>
                  <p className="text-white text-opacity-90">For Schools</p>
                </div>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-white text-sm">
                    <Icon name="Check" size={18} className="mr-2 flex-shrink-0" />
                    <span>Bulk licensing for entire programs</span>
                  </li>
                  <li className="flex items-center text-white text-sm">
                    <Icon name="Check" size={18} className="mr-2 flex-shrink-0" />
                    <span>Dedicated support for educators</span>
                  </li>
                  <li className="flex items-center text-white text-sm">
                    <Icon name="Check" size={18} className="mr-2 flex-shrink-0" />
                    <span>Custom curriculum integration</span>
                  </li>
                  <li className="flex items-center text-white text-sm">
                    <Icon name="Check" size={18} className="mr-2 flex-shrink-0" />
                    <span>Progress tracking & analytics</span>
                  </li>
                  <li className="flex items-center text-white text-sm">
                    <Icon name="Check" size={18} className="mr-2 flex-shrink-0" />
                    <span>Professional development resources</span>
                  </li>
                </ul>

                <Button
                  size="large"
                  onClick={() => navigate('/support-page')}
                  className="w-full bg-white text-blue-600 hover:bg-opacity-90 rounded-xl"
                  iconName="Mail"
                  iconPosition="left"
                >
                  Contact Us for Pricing
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {testimonials?.length > 0 && (
          <section className="py-20">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-heading font-bold text-white mb-4">
                  What Our Students Say
                </h2>
                <p className="text-xl text-blue-100">
                  Real stories from real students
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials?.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6"
                  >
                    <div className="flex items-center mb-3">
                      {[...Array(testimonial?.rating || 5)]?.map((_, i) => (
                        <Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-blue-100 mb-4 italic">
                      {testimonial?.content}
                    </p>
                    <div>
                      <div className="font-heading font-semibold text-white">
                        {testimonial?.name}
                      </div>
                      <div className="text-sm text-blue-200">
                        {testimonial?.role}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {faqs?.length > 0 && (
          <section className="py-20 bg-white bg-opacity-5">
            <div className="container mx-auto px-6 max-w-4xl">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-heading font-bold text-white mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-blue-100">
                  Everything you need to know about RoleCall
                </p>
              </div>

              <div className="space-y-4">
                {faqs?.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl overflow-hidden"
                  >
                    <button
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white hover:bg-opacity-5 transition-colors"
                    >
                      <span className="font-heading font-semibold text-white text-lg">
                        {faq?.question}
                      </span>
                      <Icon
                        name="ChevronDown"
                        size={24}
                        className={`text-white transition-transform ${activeFaq === index ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {activeFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-blue-100 leading-relaxed">
                          {faq?.answer}
                        </p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Download Section */}
        <section className="py-20" id="download">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-heading font-bold text-white mb-4">
              Take RoleCall Anywhere
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Download our mobile app and practice your musical theatre skills on the go
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#"
                onClick={handleAppDownloadClick}
                className="relative inline-flex items-center px-6 py-3 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl hover:bg-opacity-20 transition-all group"
                title="Coming Soon"
              >
                <Icon name="Apple" size={24} className="text-white mr-3" />
                <div className="text-left">
                  <div className="text-xs text-blue-200">Download on the</div>
                  <div className="text-white font-semibold">App Store</div>
                </div>
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  Coming Soon
                </div>
              </a>
              <a
                href="#"
                onClick={handleAppDownloadClick}
                className="relative inline-flex items-center px-6 py-3 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl hover:bg-opacity-20 transition-all group"
                title="Coming Soon"
              >
                <Icon name="Smartphone" size={24} className="text-white mr-3" />
                <div className="text-left">
                  <div className="text-xs text-blue-200">Get it on</div>
                  <div className="text-white font-semibold">Google Play</div>
                </div>
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  Coming Soon
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-black bg-opacity-30">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="pl-8">
                <AppImage 
                  src="/assets/images/rolecall_logo_wide-1756786874531.png" 
                  alt="RoleCall Logo - Musical Theatre Learning Platform"
                  className="h-8 w-auto mb-4"
                />
                <p className="text-blue-200 pl-0">
                  Master musical theatre with interactive lessons and professional coaching
                </p>
              </div>

              <div>
                <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/hiring-page" className="text-blue-200 hover:text-white transition-colors">
                      We're Hiring
                    </a>
                  </li>
                  <li>
                    <a href="#pricing" onClick={(e) => {
                      e?.preventDefault();
                      const pricingSection = document.getElementById('pricing');
                      if (pricingSection) {
                        pricingSection?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }} className="text-blue-200 hover:text-white transition-colors cursor-pointer">
                      Special School Pricing
                    </a>
                  </li>
                  <li>
                    <a href="/privacy-policy-page" className="text-blue-200 hover:text-white transition-colors">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href="/terms-of-service-page" className="text-blue-200 hover:text-white transition-colors">
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a href="/support-page" className="text-blue-200 hover:text-white transition-colors">
                      Support
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-heading font-semibold text-white mb-4">Connect With Us</h4>
                <div className="flex items-center space-x-4 mb-4">
                  {contact?.social?.instagram && (
                    <a href={contact?.social?.instagram} target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors" aria-label="Visit our Instagram">
                      <Icon name="Instagram" size={24} />
                    </a>
                  )}
                  {contact?.social?.twitter && (
                    <a href={contact?.social?.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors" aria-label="Visit our Twitter">
                      <Icon name="Twitter" size={24} />
                    </a>
                  )}
                  {contact?.social?.facebook && (
                    <a href={contact?.social?.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors" aria-label="Visit our Facebook">
                      <Icon name="Facebook" size={24} />
                    </a>
                  )}
                  {contact?.social?.youtube && (
                    <a href={contact?.social?.youtube} target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors" aria-label="Visit our YouTube channel">
                      <Icon name="Youtube" size={24} />
                    </a>
                  )}
                </div>
                {contact?.email && (
                  <a href={`mailto:${contact?.email}`} className="text-blue-200 hover:text-white transition-colors flex items-center">
                    <Icon name="Mail" size={16} className="mr-2" />
                    {contact?.email}
                  </a>
                )}
              </div>
            </div>

            <div className="border-t border-white border-opacity-20 pt-8 text-center">
              <p className="text-blue-200">
                © {new Date()?.getFullYear()} RoleCall. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
      {/* Backdrop overlay to close dropdown when clicking outside */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
      {/* Mobile Coming Soon Popup */}
      <AnimatePresence>
        {showMobilePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6"
            onClick={() => setShowMobilePopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-purple-600 to-pink-500 rounded-2xl p-8 max-w-sm w-full shadow-2xl"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="text-center">
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Smartphone" size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-white mb-3">
                  Coming Soon!
                </h3>
                <p className="text-white text-opacity-90 mb-6">
                  Our mobile app will be available in early 2026. Stay tuned for updates!
                </p>
                <Button
                  onClick={() => setShowMobilePopup(false)}
                  className="w-full bg-white text-purple-600 hover:bg-opacity-90"
                >
                  Got it
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PromotionalWebsite;